<template>
  <div class="map-dialog" v-show="visible">
    <div class="map-dialog-mask" @click="handleCancel"></div>
    <div class="map-dialog-container">
      <div class="map-dialog-header">
        <span>选择位置</span>
        <button @click="handleCancel">×</button>
      </div>
      <div class="map-dialog-body">
        <div class="map-search-container">
          <input
            type="text"
            v-model="searchText"
            placeholder="请输入地名"
            @keyup.enter="handleSearch"
          />
          <button @click="handleSearch">搜索</button>
        </div>
        <div class="map-content">
          <div id="map-container" style="width: 100%; height: 100%;"></div>
          <div class="search-results" v-show="searchResults.length > 0">
            <div class="results-header">
              搜索结果 (共 {{ searchResults.length }} 条)
              <span class="sort-indicator" v-if="sortedByDistance">已按距离排序</span>
            </div>
            <ul class="results-list">
              <li 
                v-for="(item, index) in searchResults" 
                :key="index"
                @click="selectResult(item, index)"
                :class="{active: selectedResultIndex === index}"
              >
                <div class="result-name">{{ item.name }}</div>
                <div class="result-address">{{ item.address }}</div>
                <div class="result-info">
                  <span class="result-city" v-if="item.city">{{ item.city }}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="map-dialog-footer">
        <button @click="handleCancel">取消</button>
        <button @click="handleConfirm">确定</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MapDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      map: null,
      marker: null,
      selectedPosition: null,
      searchText: '',
      placeSearch: null,
      geocoder: null,
      searchResults: [],
      selectedResultIndex: -1,
      sortedByDistance: false,
      currentCity: '',
      currentLocation: [113.526732, 34.280633] // 默认位置
    };
  },
  watch: {
    visible(val) {
      if (val) {
        console.log(val,'visible changed to true');
        this.$nextTick(() => {
          this.map = null;
          this.marker = null;
          this.selectedPosition = null;
          this.searchText = '';
          this.searchResults = [];
          this.selectedResultIndex = -1;
          this.sortedByDistance = false;
          this.initMap();
        });
      }
    }
  },
  methods: {
    initMap() {
      // 初始化地图，使用默认位置
      this.map = new AMap.Map('map-container', {
        zoom: 15,
        center: this.currentLocation,
        viewMode: '2D'
      });

      // 加载所需插件
      AMap.plugin([
        'AMap.Geocoder', 
        'AMap.PlaceSearch', 
        'AMap.AutoComplete',
        'AMap.GeometryUtil'
      ], () => {
        this.geocoder = new AMap.Geocoder();
        this.placeSearch = new AMap.PlaceSearch({
          map: this.map,
          pageSize: 50,
          pageIndex: 1,
          city: this.currentCity,
          panel: null
        });
      });
      
      // 添加标记
      this.marker = new AMap.Marker({
        position: this.currentLocation,
        map: this.map
      });

      // 点击地图添加标记
      // this.map.on('click', (e) => {
      //   this.selectedPosition = [e.lnglat.getLng(), e.lnglat.getLat()];
      //   this.marker.setPosition(this.selectedPosition);
      //   this.searchResults = [];
      //   this.selectedResultIndex = -1;
      //   this.sortedByDistance = false;
        
      //   // 反向地理编码获取地址信息
      //   this.geocoder.getAddress(this.selectedPosition, (status, result) => {
      //     if (status === 'complete' && result.regeocode) {
      //       this.searchText = result.regeocode.formattedAddress;
      //       // 更新当前城市信息
      //       const city = result.regeocode.addressComponent.city || 
      //                   result.regeocode.addressComponent.province;
      //       if (city && city !== this.currentCity) {
      //         this.currentCity = city;
      //         this.placeSearch.setCity(this.currentCity);
      //       }
      //     }
      //   });
      // });
    },
    
    // 计算两点之间的距离（公里）
    calculateDistance(point1, point2) {
      const getLng = (p) => p.getLng ? p.getLng() : (Array.isArray(p) ? p[0] : p.lng);
      const getLat = (p) => p.getLat ? p.getLat() : (Array.isArray(p) ? p[1] : p.lat);
      
      const lng1 = getLng(point1);
      const lat1 = getLat(point1);
      const lng2 = getLng(point2);
      const lat2 = getLat(point2);
      
      return (AMap.GeometryUtil.distance(
        new AMap.LngLat(lng1, lat1),
        new AMap.LngLat(lng2, lat2)
      ) / 1000).toFixed(2);
    },
    
    handleSearch() {
      if (!this.searchText.trim()) return;
      
      // 重置状态
      this.searchResults = [];
      this.selectedResultIndex = -1;
      this.sortedByDistance = false;
      
      // 优先在当前城市搜索
      this.placeSearch.setCity(this.currentCity);
      this.searchInCity(this.searchText);
    },
    
    // 在当前城市搜索
    searchInCity(keyword) {
      this.placeSearch.search(keyword, (status, result) => {
        if (status === 'complete' && result.poiList && result.poiList.pois.length) {
          this.processSearchResults(result.poiList.pois);
        } else {
          // 如果当前城市没有结果，再尝试全国范围搜索
          this.searchNationwide(keyword);
        }
      });
    },
    
    // 全国范围搜索
    searchNationwide(keyword) {
      this.placeSearch.setCity('全国');
      this.placeSearch.search(keyword, (status, result) => {
        if (status === 'complete' && result.poiList && result.poiList.pois.length) {
          this.processSearchResults(result.poiList.pois);
        } else {
          // 如果没有搜索结果，尝试地理编码
          this.tryGeocoding(keyword);
        }
      });
    },
    
    // 处理搜索结果
    processSearchResults(pois) {
      // 计算每个结果与当前位置的距离
      this.searchResults = pois.map(poi => {
        const distance = this.calculateDistance(
          this.currentLocation,
          [poi.location.lng, poi.location.lat]
        );
        return {
          name: poi.name,
          address: poi.address,
          lnglat: [poi.location.lng, poi.location.lat],
          distance: distance,
          city: poi.cityname || poi.adname
        };
      });
      
      // 优先显示当前城市的结果
      const currentCityResults = this.searchResults.filter(item => 
        item.city && item.city.includes(this.currentCity)
      );
      
      // 非当前城市的结果
      const otherCityResults = this.searchResults.filter(item => 
        !item.city || !item.city.includes(this.currentCity)
      );
      
      // 合并结果：当前城市的结果在前，并按距离排序
      this.searchResults = [
        ...currentCityResults.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance)),
        ...otherCityResults.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance))
      ];
      
      this.sortedByDistance = true;
      
      // 默认选中第一个结果（最近的）
      if (this.searchResults.length > 0) {
        this.selectResult(this.searchResults[0], 0);
      }
    },
    
    // 尝试地理编码
    tryGeocoding(keyword) {
      this.geocoder.getLocation(keyword, (status, result) => {
        if (status === 'complete' && result.geocodes.length) {
          const geocode = result.geocodes[0];
          const distance = this.calculateDistance(
            this.currentLocation,
            [geocode.location.lng, geocode.location.lat]
          );
          this.searchResults = [{
            name: keyword,
            address: geocode.formattedAddress,
            lnglat: [geocode.location.lng, geocode.location.lat],
            distance: distance,
            city: geocode.addressComponent.city || geocode.addressComponent.province
          }];
          this.selectResult(this.searchResults[0], 0);
        } else {
          this.$message.error('未找到相关位置');
          this.searchResults = [];
          this.selectedResultIndex = -1;
          this.sortedByDistance = false;
        }
      });
    },
    
    selectResult(item, index) {
      this.selectedResultIndex = index;
      this.selectedPosition = item.lnglat;
      this.marker.setPosition(this.selectedPosition);
      this.map.setCenter(this.selectedPosition);
      this.searchText = item.name;
    },
    
    handleCancel() {
      this.$emit('update:visible', false);
      this.$emit('cancel');
    },
    
    handleConfirm() {
      if (this.selectedPosition) {
        this.$emit('confirm', {
          lnglat: this.selectedPosition,
          address: this.searchText,
          city: this.currentCity
        });
      } else {
        const center = this.map.getCenter();
        this.selectedPosition = [center.getLng(), center.getLat()];
        
        // 获取当前位置的地址
        this.geocoder.getAddress(this.selectedPosition, (status, result) => {
          if (status === 'complete' && result.regeocode) {
            this.searchText = result.regeocode.formattedAddress;
          }
          this.$emit('confirm', {
            lnglat: this.selectedPosition,
            address: this.searchText,
            city: this.currentCity
          });
        });
        return;
      }
      this.$emit('update:visible', false);
    }
  },
  beforeDestroy() {
    if (this.map) {
      this.map.destroy();
    }
    // 移回原位，避免内存泄漏
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el)
    }
  },
  mounted() {
    // 挂载到 body，脱离父级层叠上下文
    document.body.appendChild(this.$el)
  },
};
</script>

<style scoped>
.map-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999999!important;
}

.map-dialog-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
}

.map-dialog-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 1400px;
  max-width: 95%;
  height: 95vh;
  max-height: 95vh;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

.map-dialog-header {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
}

.map-dialog-header button {
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f2f5;
  color: #606266;
  font-size: 18px;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
  line-height: 1;
}

.map-dialog-header button:hover {
  background: #ff4d4f;
  color: #fff;
}

.map-dialog-body {
  padding: 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.map-search-container {
  display: flex;
  margin-bottom: 15px;
  align-items: center;
}

.map-search-container input {
  flex: 1;
  padding: 8px 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  outline: none;
}

.map-search-container button {
  margin-left: 10px;
  padding: 5px 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #409eff;
  color: white;
  cursor: pointer;
}

.map-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

#map-container {
  flex: 1;
  height: 100%;
}

.search-results {
  width: 300px;
  margin-left: 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.results-header {
  padding: 10px 15px;
  background: #f5f7fa;
  border-bottom: 1px solid #dcdfe6;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sort-indicator {
  font-size: 12px;
  color: #67c23a;
  font-weight: normal;
}

.results-list {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
}

.results-list li {
  padding: 10px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.results-list li:hover {
  background: #f5f7fa;
}

.results-list li.active {
  background: #ecf5ff;
  color: #409eff;
}

.result-name {
  font-weight: bold;
  margin-bottom: 5px;
}

.result-address {
  font-size: 12px;
  color: #909399;
  margin-bottom: 5px;
}

.result-info {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.result-city {
  color: #606266;
}

.map-dialog-footer {
  padding: 15px;
  border-top: 1px solid #eee;
  text-align: right;
}

.map-dialog-footer button {
  margin-left: 10px;
  padding: 5px 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.map-dialog-footer button:last-child {
  color: #fff;
  background: #409eff;
  border-color: #409eff;
}

@media (max-width: 768px) {
  .map-content {
    flex-direction: column;
  }
  
  .search-results {
    width: auto;
    margin-left: 0;
    margin-top: 15px;
    max-height: 200px;
  }
}
</style>